import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "react-ikonate";
import styles from "./index.module.css";

export default function TableFilterDropdown({
  title,
  options,
  selectedValues,
  implicitCheckedValues,
  isExplicitSelection = false,
  onChange,
  onMenuStateChange,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [draftValues, setDraftValues] = useState(selectedValues);
  const containerRef = useRef(null);
  const menuRef = useRef(null);

  const optionValues = useMemo(() => options.map((option) => option.value), [options]);
  const implicitSelectedValues = useMemo(() => {
    if (implicitCheckedValues && implicitCheckedValues.length > 0) {
      return implicitCheckedValues.filter((value) => optionValues.includes(value));
    }
    return optionValues;
  }, [implicitCheckedValues, optionValues]);
  const appliedSelectedValues = useMemo(() => {
    if (selectedValues.length > 0) {
      return selectedValues.filter((value) => optionValues.includes(value));
    }
    return implicitSelectedValues;
  }, [selectedValues, optionValues, implicitSelectedValues]);

  const makeOpeningDraft = useCallback(() => {
    // Empty selectedValues means the currently displayed row-values are selected.
    if (selectedValues.length === 0) {
      return implicitSelectedValues;
    }

    // Keep only options that are currently available in this menu.
    return selectedValues.filter((value) => optionValues.includes(value));
  }, [implicitSelectedValues, optionValues, selectedValues]);

  useEffect(() => {
    const onMouseDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setDraftValues(makeOpeningDraft());
    }
  }, [makeOpeningDraft, open]);

  useEffect(() => {
    if (!onMenuStateChange) {
      return;
    }

    if (!open) {
      onMenuStateChange(false, 0);
      return;
    }

    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    onMenuStateChange(true, menuHeight);
  }, [onMenuStateChange, open, options.length]);

  const selectedSet = new Set(draftValues);
  const allSelected =
    optionValues.length > 0 &&
    optionValues.every((value) => selectedSet.has(value));

  const appliedAllSelected =
    optionValues.length > 0 &&
    optionValues.every((value) => appliedSelectedValues.includes(value));
  const isPartialSelection = appliedSelectedValues.length > 0 && !appliedAllSelected;
  const isActiveSelection = isExplicitSelection && isPartialSelection;

  return (
    <div ref={containerRef} className={classNames(styles.container, className)}>
      <button
        className={classNames(styles.button, {
          [styles.active]: isActiveSelection,
        })}
        onClick={(event) => {
          event.preventDefault();
          if (open) {
            setOpen(false);
          } else {
            setDraftValues(makeOpeningDraft());
            setOpen(true);
          }
        }}
      >
        <span className={styles.label}>{title}</span>
        {isActiveSelection && (
          <span className={styles.count}>{appliedSelectedValues.length}</span>
        )}
        <ChevronDown className={styles.icon} />
      </button>

      {open && (
        <div ref={menuRef} className={styles.menu}>
          {options.length === 0 ? (
            <div className={styles.empty}>No options</div>
          ) : (
            <>
              <label className={styles.option}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => {
                    setDraftValues(optionValues);
                    onChange(optionValues);
                  }}
                />
                <span>All</span>
              </label>

              {options.map((option) => (
                <label key={option.value} className={styles.option}>
                  <input
                    type="checkbox"
                    checked={selectedSet.has(option.value)}
                    onChange={() => {
                      let next;
                      if (selectedSet.has(option.value)) {
                        next = draftValues.filter((value) => value !== option.value);
                      } else {
                        next = [...draftValues, option.value];
                      }
                      setDraftValues(next);

                      // Apply immediately unless this would apply zero filters.
                      if (next.length > 0) {
                        onChange(next);
                      }
                    }}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </>
          )}

          <div className={styles.footer}>
            <button
              className={styles.clearButton}
              onClick={(event) => {
                event.preventDefault();
                setDraftValues([]);
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
